import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import BasicLayout from '../layouts/BasicLayout';
import BlankLayout from '../layouts/BlankLayout';
import ProtectedRoute from '../components/ProtectedRoute';
import type { RouteConfig } from '../types/ui';

// Route-level code splitting: each page is its own chunk (loaded on navigation).
const Dashboard = lazy(() => import('../pages/Dashboard'));
const NotFound = lazy(() => import('../pages/NotFound'));
const Login = lazy(() => import('../pages/Login'));
const UserList = lazy(() => import('../pages/users/UserList'));
const UserDetail = lazy(() => import('../pages/users/UserDetail'));
const UserForm = lazy(() => import('../pages/users/UserForm'));
const RoleList = lazy(() => import('../pages/roles/RoleList'));
const RoleDetail = lazy(() => import('../pages/roles/RoleDetail'));
const RoleForm = lazy(() => import('../pages/roles/RoleForm'));
const AiAccess = lazy(() => import('../pages/ai-access/AiAccess'));
const AiTools = lazy(() => import('../pages/ai-tools/AiTools'));
const ZhipuAIKeyPool = lazy(() => import('../pages/ai-access/ZhipuAIKeyPool'));
const GitHub = lazy(() => import('../pages/github/GitHub'));
const GitHubManagement = lazy(() => import('../pages/github/GitHubManagement'));
const ApiKeyList = lazy(() => import('../pages/api-keys/ApiKeyList'));
const FileList = lazy(() => import('../pages/files/FileList'));
const FileDetail = lazy(() => import('../pages/files/FileDetail'));
const AppList = lazy(() => import('../pages/apps'));
const CreateApp = lazy(() => import('../pages/apps/create'));
const AppDetail = lazy(() => import('../pages/apps/AppDetail'));
const AppEdit = lazy(() => import('../pages/apps/AppEdit'));
const DocumentList = lazy(() => import('../pages/documents/DocumentList'));
const DocumentUpload = lazy(() => import('../pages/documents/DocumentUpload'));
const DocumentDetail = lazy(() => import('../pages/documents/DocumentDetail'));
const DocumentEdit = lazy(() => import('../pages/documents/DocumentEdit'));
const SyncLogs = lazy(() => import('../pages/sync-logs'));
const HabbySearch = lazy(() => import('../pages/habby-search/HabbySearch'));
const DictionaryList = lazy(() => import('../pages/dictionary/DictionaryList'));
const GuideList = lazy(() => import('../pages/guides/GuideList'));
const GuideForm = lazy(() => import('../pages/guides/GuideForm'));
const GuideDetail = lazy(() => import('../pages/guides/GuideDetail'));
const PlatformManagement = lazy(() => import('../pages/scim/PlatformManagement'));
const MyPermissions = lazy(() => import('../pages/scim/MyPermissions'));
const DatasetAccess = lazy(() => import('../pages/dataset-access/DatasetAccess'));
const DataSourceManagement = lazy(() => import('../pages/dataset-access/DataSourceManagement'));
const DataSourceDetail = lazy(() => import('../pages/dataset-access/DataSourceDetail'));
const AccessRoleForm = lazy(() => import('../pages/dataset-access/AccessRoleForm'));
const ThinkingDataAccess = lazy(() => import('../pages/thinkingdata-access/ThinkingDataAccess'));
const ThinkingDataAdmin = lazy(() => import('../pages/thinkingdata-access/ThinkingDataAdmin'));
const MarketingAuthorizations = lazy(() => import('../pages/marketing-authorizations/MarketingAuthorizations'));
const MarketingPermissionAdmin = lazy(() => import('../pages/marketing-authorizations/MarketingPermissionAdmin'));
const GameManagement = lazy(() => import('../pages/games/GameManagement'));
const GameDetail = lazy(() => import('../pages/games/GameDetail'));
const GameAliasManagement = lazy(() => import('../pages/games/GameAliasManagement'));
const GroupList = lazy(() => import('../pages/google-workspace-groups/GroupList'));
const GroupDetail = lazy(() => import('../pages/google-workspace-groups/GroupDetail'));
const GroupForm = lazy(() => import('../pages/google-workspace-groups/GroupForm'));
const OnsiteNas = lazy(() => import('../pages/onsite-nas/OnsiteNas'));

/**
 * Routing policy (layout composition):
 * - Public: /login
 * - Authenticated shell: everything under BasicLayout (RequireAuth on parent)
 * - Admin: pathless layout group (RequireAdmin + Outlet)
 * - Super-admin: pathless layout group (RequireSuperAdmin + Outlet)
 *
 * New pages under BasicLayout are auth-protected by default.
 * Elevate into admin/super-admin groups when needed — do not wrap every leaf.
 */
const routes: RouteConfig[] = [
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <BasicLayout />
      </ProtectedRoute>
    ),
    meta: {
      title: '首页',
      breadcrumb: false,
    },
    children: [
      // ── Any authenticated user ──────────────────────────────────────
      {
        path: '',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <Dashboard />,
        meta: { title: '首页' },
      },
      {
        path: 'habby-search',
        element: <HabbySearch />,
        meta: { title: 'Habby Search' },
      },
      {
        path: 'ai-access',
        element: <AiAccess />,
        meta: { title: 'AI 权限管理' },
      },
      {
        path: 'ai-tools',
        element: <AiTools />,
        meta: { title: 'AI 工具配置文档' },
      },
      {
        path: 'vibe-coding',
        element: <Navigate to="/ai-access" replace />,
        meta: { title: 'AI 权限管理' },
      },
      {
        path: 'dataset-access',
        element: <DatasetAccess />,
        meta: { title: '数据集' },
      },
      {
        path: 'thinkingdata-access',
        element: <ThinkingDataAccess />,
        meta: { title: 'TGA' },
      },
      {
        path: 'marketing-authorizations',
        element: <MarketingAuthorizations />,
        meta: { title: '市场渠道' },
      },
      {
        path: 'github',
        element: <GitHub />,
        meta: { title: 'GitHub' },
      },
      {
        path: 'scim/my-permissions',
        element: <MyPermissions />,
        meta: { title: '应用开通' },
      },
      {
        path: 'keys',
        element: <ApiKeyList />,
        meta: { title: 'API Key' },
      },
      {
        path: 'files',
        element: <FileList />,
        meta: { title: '文件' },
      },
      {
        path: 'onsite-nas',
        element: <OnsiteNas />,
        meta: { title: 'NAS' },
      },
      {
        path: 'files/:id',
        element: <FileDetail />,
        meta: { title: '文件详情', hideInMenu: true },
      },
      {
        path: 'documents',
        element: <DocumentList />,
        meta: { title: '文档' },
      },
      {
        path: 'documents/:documentId',
        element: <DocumentDetail />,
        meta: { title: '文档详情', hideInMenu: true },
      },
      {
        path: 'guides',
        element: <GuideList />,
        meta: { title: '指南' },
      },
      {
        path: 'guides/new',
        element: <GuideForm />,
        meta: { title: '新建文档', hideInMenu: true },
      },
      {
        path: 'guides/:slug',
        element: <GuideDetail />,
        meta: { title: '文档详情', hideInMenu: true },
      },
      {
        path: 'guides/:slug/edit',
        element: <GuideForm />,
        meta: { title: '编辑文档', hideInMenu: true },
      },

      // ── Admin only ──────────────────────────────────────────────────
      {
        element: <ProtectedRoute requireAdmin />,
        children: [
          {
            path: 'users',
            element: <UserList />,
            meta: { title: '用户管理' },
          },
          {
            path: 'users/new',
            element: <UserForm />,
            meta: { title: '新增用户', hideInMenu: true },
          },
          {
            path: 'users/:id',
            element: <UserDetail />,
            meta: { title: '用户详情', hideInMenu: true },
          },
          {
            path: 'users/:id/edit',
            element: <UserForm />,
            meta: { title: '编辑用户', hideInMenu: true },
          },
          {
            path: 'roles',
            element: <RoleList />,
            meta: { title: '角色管理' },
          },
          {
            path: 'roles/new',
            element: <RoleForm />,
            meta: { title: '新增角色', hideInMenu: true },
          },
          {
            path: 'roles/:id',
            element: <RoleDetail />,
            meta: { title: '角色详情', hideInMenu: true },
          },
          {
            path: 'roles/:id/edit',
            element: <RoleForm />,
            meta: { title: '编辑角色', hideInMenu: true },
          },
          {
            path: 'github-management',
            element: <GitHubManagement />,
            meta: { title: 'GitHub 管理' },
          },
          {
            path: 'scim/platforms',
            element: <PlatformManagement />,
            meta: { title: 'SCIM 平台配置' },
          },
          {
            path: 'ai-access/zhipuai-pool',
            element: <ZhipuAIKeyPool />,
            meta: { title: '智谱 AI Key 池管理' },
          },
          {
            path: 'thinkingdata-admin',
            element: <ThinkingDataAdmin />,
            meta: { title: 'TGA 管理', hideInMenu: true },
          },
          {
            path: 'marketing-permissions-admin',
            element: <MarketingPermissionAdmin />,
            meta: { title: '市场渠道权限管理', hideInMenu: true },
          },
          {
            path: 'dataset-sources',
            element: <DataSourceManagement />,
            meta: { title: '数据源管理' },
          },
          {
            path: 'dataset-sources/new',
            element: <DataSourceDetail />,
            meta: { title: '创建数据源', hideInMenu: true },
          },
          {
            path: 'dataset-sources/:id',
            element: <DataSourceDetail />,
            meta: { title: '数据源详情', hideInMenu: true },
          },
          {
            path: 'dataset-sources/:sourceId/roles/new',
            element: <AccessRoleForm />,
            meta: { title: '添加访问角色', hideInMenu: true },
          },
          {
            path: 'dataset-sources/:sourceId/roles/:roleId/edit',
            element: <AccessRoleForm />,
            meta: { title: '编辑访问角色', hideInMenu: true },
          },
          {
            path: 'games',
            element: <GameManagement />,
            meta: { title: '游戏管理' },
          },
          {
            path: 'games/new',
            element: <GameDetail />,
            meta: { title: '添加游戏', hideInMenu: true },
          },
          {
            path: 'games/:id',
            element: <GameDetail />,
            meta: { title: '编辑游戏', hideInMenu: true },
          },
          {
            path: 'game-aliases',
            element: <GameAliasManagement />,
            meta: { title: '游戏归属' },
          },
          {
            path: 'google-workspace-groups',
            element: <GroupList />,
            meta: { title: 'Google Workspace 组管理' },
          },
          {
            path: 'google-workspace-groups/new',
            element: <GroupForm />,
            meta: { title: '创建 Google Workspace 组', hideInMenu: true },
          },
          {
            path: 'google-workspace-groups/:groupKey',
            element: <GroupDetail />,
            meta: { title: 'Google Workspace 组详情', hideInMenu: true },
          },
          {
            path: 'google-workspace-groups/:groupKey/edit',
            element: <GroupForm />,
            meta: { title: '编辑 Google Workspace 组', hideInMenu: true },
          },
          {
            path: 'app-management',
            element: <AppList />,
            meta: { title: '应用管理' },
          },
          {
            path: 'app-management/create',
            element: <CreateApp />,
            meta: { title: '创建应用', hideInMenu: true },
          },
          {
            path: 'app-management/:id',
            element: <AppDetail />,
            meta: { title: '应用详情', hideInMenu: true },
          },
          {
            path: 'app-management/:id/edit',
            element: <AppEdit />,
            meta: { title: '编辑应用', hideInMenu: true },
          },
          {
            path: 'documents/upload',
            element: <DocumentUpload />,
            meta: { title: '上传文档', hideInMenu: true },
          },
          {
            path: 'documents/:documentId/edit',
            element: <DocumentEdit />,
            meta: { title: '编辑文档', hideInMenu: true },
          },
          {
            path: 'sync-logs',
            element: <SyncLogs />,
            meta: { title: '同步日志' },
          },
        ],
      },

      // ── Super-admin only ────────────────────────────────────────────
      {
        element: <ProtectedRoute requireSuperAdmin />,
        children: [
          {
            path: 'dictionary',
            element: <DictionaryList />,
            meta: { title: '数据字典' },
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    element: <Login />,
    meta: {
      title: '登录',
    },
  },
  {
    path: '*',
    element: <BlankLayout />,
    children: [
      {
        path: '*',
        element: <NotFound />,
        meta: {
          title: '404',
        },
      },
    ],
  },
];

export default routes;
